/**
 * VIN Validation Service
 * Handles VIN validation and decoding API calls
 */

import { makeAuthenticatedRequest, createApiError } from './apiService';
import { API_ENDPOINTS } from '../config/api';

// TypeScript interfaces matching backend DTOs

export interface VINValidationRequest {
  vin: string;
}

export interface EngineInfo {
  cylinders?: string;
  displacementL?: string;
  displacementCC?: string;
  fuelType?: string;
  horsepower?: string;
  model?: string;
  configuration?: string;
  otherInfo?: string;
}

export interface TransmissionInfo {
  style?: string;
  speeds?: string;
}

export interface PlantInfo {
  city?: string;
  state?: string;
  country?: string;
  companyName?: string;
}

export interface SafetyFeatures {
  abs?: string;
  esc?: string;
  tractionControl?: string;
  tpms?: string;
  autoReverse?: string;
  edr?: string;
  keylessIgnition?: string;
}

export interface AirbagConfig {
  front?: string;
  side?: string;
  curtain?: string;
  knee?: string;
  seatBelt?: string;
}

export interface DriverAssist {
  acc?: string;
  cib?: string;
  fcw?: string;
  dbs?: string;
  bsw?: string;
  ldw?: string;
  lka?: string;
  backupCamera?: string;
  parkingAssist?: string;
  rearCrossTraffic?: string;
  paeb?: string;
}

export interface LightingFeatures {
  drl?: string;
  headlampSource?: string;
  autoBeam?: string;
}

export interface VINDecodeResult {
  // Basic VIN structure
  vin: string;
  year?: number;
  make?: string;
  ncicMakeCode?: string;  // NCIC 4-character make code (e.g., "CHEV" for Chevrolet)
  model?: string;
  manufacturer?: string;
  country?: string;
  wmi?: string;  // World Manufacturer Identifier
  vds?: string;  // Vehicle Descriptor Section
  vis?: string;  // Vehicle Identifier Section
  assemblyPlant?: string;
  serialNumber?: string;
  checkDigit?: string;
  checkDigitValid: boolean;

  // Enhanced NHTSA data
  bodyClass?: string;
  trim?: string;
  vehicleType?: string;
  engine?: EngineInfo;
  transmission?: TransmissionInfo;
  plant?: PlantInfo;
  driveType?: string;
  fuelType?: string;
  doors?: string;
  seats?: string;
  seatRows?: string;
  gvwr?: string;
  curbWeight?: string;
  brakeSystem?: string;

  // Safety and advanced features
  safety?: SafetyFeatures;
  airbags?: AirbagConfig;
  driverAssist?: DriverAssist;
  lighting?: LightingFeatures;

  // Raw NHTSA data
  rawData?: Record<string, any>;

  // Metadata
  source?: string;  // "Basic", "NHTSA", or "Both"
  fromCache: boolean;
  validationWarnings?: string[];

  // Extended API fields (from decode-extended endpoint)
  modelYear?: string;        // String version from NHTSA
  manufacturerName?: string; // Full manufacturer name from NHTSA
  error?: string;
}

export interface VINValidationResponse {
  isValid: boolean;
  errorCode?: string;
  errorMessage?: string;
  decodeResult?: VINDecodeResult;

  /** Unique Report ID for this decode result (only set for authenticated requests) */
  reportId?: string;

  /** Timestamp when the data was fetched/stored */
  fetchedAt?: string;

  /** Whether this result was retrieved from database cache */
  wasCached?: boolean;

  /** Cost charged for this decode (0 if cached) */
  billingAmount?: number;
}

export interface VinDecodePricingResponse {
  costPerVin: number;
  serviceType: string;
}

/**
 * Fetch VIN Decode pricing (flat rate per lookup)
 * Used to display estimated cost before search
 */
export const fetchVinDecodePricing = async (): Promise<VinDecodePricingResponse> => {
  const response = await makeAuthenticatedRequest(
    API_ENDPOINTS.VIN.PRICING,
    { method: 'GET' }
  );

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};

/**
 * Validate and decode a VIN (full decode with DataOne + NHTSA)
 * @param vin - The VIN to validate
 * @returns Promise with validation response containing decoded vehicle data
 */
export const validateVIN = async (vin: string): Promise<VINValidationResponse> => {
  try {
    const response = await makeAuthenticatedRequest(
      API_ENDPOINTS.VIN.VALIDATE,
      {
        method: 'POST',
        body: JSON.stringify({ vin }),
      }
    );

    if (!response.ok) {
      const error = await createApiError(response);
      throw error;
    }

    const data: VINValidationResponse = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Quick VIN validation using local decoder only - instant response
 * Use this for preview cards that need immediate display
 * Returns basic info: year, manufacturer, check digit validation
 * @param vin - The VIN to validate
 * @returns Promise with validation response containing basic vehicle data
 */
export const validateVINQuick = async (vin: string): Promise<VINValidationResponse> => {
  try {
    const response = await makeAuthenticatedRequest(
      API_ENDPOINTS.VIN.VALIDATE_QUICK,
      {
        method: 'POST',
        body: JSON.stringify({ vin }),
      }
    );

    if (!response.ok) {
      const error = await createApiError(response);
      throw error;
    }

    const data: VINValidationResponse = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Decode VIN with extended NHTSA data
 * @param vin - The VIN to decode
 * @returns Promise with extended decode result
 */
export const decodeVINExtended = async (vin: string): Promise<VINDecodeResult> => {
  try {
    // Backend expects GET request with query parameter
    const url = `${API_ENDPOINTS.VIN.DECODE_EXTENDED}?vin=${encodeURIComponent(vin)}`;
    const response = await makeAuthenticatedRequest(url, {
      method: 'GET',
    });

    if (!response.ok) {
      const error = await createApiError(response);
      throw error;
    }

    const data: VINDecodeResult = await response.json();
    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get user-friendly error message from VIN validation error code
 * @param errorCode - NMVTIS error code
 * @returns Human-readable error message
 */
export const getVINErrorMessage = (errorCode: string): string => {
  const errorMessages: Record<string, string> = {
    '100': 'VIN is required',
    '200': 'VIN contains invalid characters (I, O, Q are not allowed)',
    '20A': 'VIN is longer than 17 characters',
    '20B': 'VIN is shorter than 17 characters',
    '21A': 'VIN check digit validation failed',
    '21C': 'VIN did not decode and check digit failed',
    '228': 'VIN contains non-printable characters',
  };

  return errorMessages[errorCode] || 'Invalid VIN format';
};

/**
 * Format decoded VIN data for display
 * @param decodeResult - The decoded VIN result
 * @returns Formatted string for display
 */
export const formatVehicleInfo = (decodeResult: VINDecodeResult): string => {
  const parts: string[] = [];

  if (decodeResult.year) parts.push(decodeResult.year.toString());
  if (decodeResult.make) parts.push(decodeResult.make);
  if (decodeResult.model) parts.push(decodeResult.model);

  return parts.length > 0 ? parts.join(' ') : 'Vehicle information unavailable';
};

/**
 * Check if VIN format is valid (basic client-side validation)
 * Supports both modern 17-character VINs and older shorter VINs (pre-1981)
 * @param vin - The VIN to check
 * @returns True if VIN appears to be in valid format
 */
export const isVINFormatValid = (vin: string): boolean => {
  if (!vin) return false;

  // VIN must be at least 5 characters (older VINs varied in length)
  if (vin.length < 5) return false;

  // VIN cannot contain I, O, or Q
  const invalidChars = /[IOQ]/i;
  if (invalidChars.test(vin)) return false;

  // VIN must be alphanumeric
  const validFormat = /^[A-HJ-NPR-Z0-9]+$/i;
  return validFormat.test(vin);
};

// PDF Generation Types
export interface GenerateVinDecodePdfRequest {
  vin: string;
  requestedBy?: string;
  referenceNote?: string;
  reportId?: string;
}

/**
 * Generate a PDF report for VIN decode data
 * @param request - The PDF generation request containing VIN and optional metadata
 * @returns Promise that resolves when download is triggered
 */
export const generateVinDecodePdf = async (
  request: GenerateVinDecodePdfRequest
): Promise<void> => {
  try {
    const response = await makeAuthenticatedRequest(
      API_ENDPOINTS.VIN.GENERATE_PDF,
      {
        method: 'POST',
        body: JSON.stringify(request),
      }
    );

    if (!response.ok) {
      const error = await createApiError(response);
      throw error;
    }

    // Get the PDF blob from the response
    const blob = await response.blob();

    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers.get('Content-Disposition');
    let fileName = `VIN_Decode_Report_${request.vin}_${new Date().toISOString().split('T')[0]}.pdf`;

    if (contentDisposition) {
      const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
      if (fileNameMatch && fileNameMatch[1]) {
        fileName = fileNameMatch[1];
      }
    }

    // Create a temporary URL for the blob
    const url = window.URL.createObjectURL(blob);

    // Create a temporary anchor element and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    throw error;
  }
};

const vinService = {
  validateVIN,
  validateVINQuick,
  decodeVINExtended,
  getVINErrorMessage,
  formatVehicleInfo,
  isVINFormatValid,
  generateVinDecodePdf,
};

export default vinService;
